import { useEffect, useRef } from 'react';

import { mapAmendmentPolicyPersonsToMedicalInsureds } from '../helpers/mapAmendmentPolicyPersonsToMedicalInsureds';
import { FormValues, MedicalInsuredFormRow } from '../types';
import { AmendmentPolicyPerson } from '../types/getAmendmentPolicyPersonsResponse.types';

import { useFormikContext } from 'formik';

const mergeMedicalInsuredRow = (
  mapped: MedicalInsuredFormRow,
  current?: MedicalInsuredFormRow
): MedicalInsuredFormRow => ({
  ...mapped,
  cardNumber: current?.cardNumber || mapped.cardNumber,
  dateOfBirth: current?.dateOfBirth || mapped.dateOfBirth,
  passportNumber: current?.passportNumber || mapped.passportNumber,
  continuity: current?.continuity || mapped.continuity,
  tpaReference1: current?.tpaReference1 || mapped.tpaReference1,
  tpaReference2: current?.tpaReference2 || mapped.tpaReference2,
  grApplicable: current?.grApplicable || mapped.grApplicable,
  exclusions: current?.exclusions || mapped.exclusions,
  remarks: current?.remarks || mapped.remarks,
  personType: current?.personType || mapped.personType,
  isNew: current?.isNew ?? mapped.isNew,
  isRemoved: current?.isRemoved ?? mapped.isRemoved
});

const buildPolicyPersonsKey = (policyPersons: AmendmentPolicyPerson[]) =>
  policyPersons
    .map((person) => `${person.id ?? ''}:${person.personID ?? ''}`)
    .join('|');

const getUserAddedAdherents = (
  current: MedicalInsuredFormRow[],
  mappedCount: number
) =>
  current.slice(mappedCount).filter((insured) => insured.isNew && !insured.isRemoved);

const buildHydratedMedicalInsureds = (
  mapped: MedicalInsuredFormRow[],
  current: MedicalInsuredFormRow[]
) => {
  const mergedBase = mapped.map((insured, index) =>
    mergeMedicalInsuredRow(insured, current[index])
  );

  return [...mergedBase, ...getUserAddedAdherents(current, mapped.length)];
};

export const useHydrateMedicalInsuredsFromQuery = (
  policyPersons: AmendmentPolicyPerson[] | undefined,
  isCertificateAmendment = false
) => {
  const { setFieldValue, values } = useFormikContext<FormValues>();
  const hydratedPolicyKeyRef = useRef<string>('');

  useEffect(() => {
    if (!policyPersons || policyPersons.length === 0) {
      return;
    }

    const mapped = mapAmendmentPolicyPersonsToMedicalInsureds(policyPersons);

    if (mapped.length === 0) {
      return;
    }

    const policyKey = buildPolicyPersonsKey(policyPersons);
    const current = values.medicalInsureds ?? [];

    if (isCertificateAmendment) {
      if (policyKey === hydratedPolicyKeyRef.current) {
        return;
      }

      setFieldValue(
        'medicalInsureds',
        buildHydratedMedicalInsureds(mapped, current),
        false
      );
      hydratedPolicyKeyRef.current = policyKey;

      return;
    }

    if (
      current.length > mapped.length &&
      current.some((insured) => insured.isNew)
    ) {
      hydratedPolicyKeyRef.current = policyKey;

      return;
    }

    if (policyKey === hydratedPolicyKeyRef.current) {
      return;
    }

    setFieldValue('medicalInsureds', mapped, false);
    hydratedPolicyKeyRef.current = policyKey;
  }, [isCertificateAmendment, policyPersons, setFieldValue]);
};
